/**
 * Admin page — view and triage community suggestions.
 * Protected by Supabase Auth (email magic link).
 * Public users cannot reach this page's data via RLS.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { Session } from '@supabase/supabase-js'
import type { Suggestion, ReviewStatus } from '../types'
import { SUGGESTION_TYPE_LABELS } from '../types'

// ── Auth gate ─────────────────────────────────────────────────────────────────

export function AdminPage() {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s)
    })
    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-400">
        Loading…
      </div>
    )
  }

  if (!session) return <LoginForm />
  return <SuggestionQueue session={session} />
}

// ── Login form (email magic link) ─────────────────────────────────────────────

function LoginForm() {
  const [email, setEmail]     = useState('')
  const [sent, setSent]       = useState(false)
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    })
    setLoading(false)
    if (error) {
      setError(error.message)
    } else {
      setSent(true)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 max-w-sm w-full space-y-6">
        <div>
          <h1 className="text-xl font-bold text-magar-700">Admin Login</h1>
          <p className="text-sm text-stone-500 mt-1">
            Enter your admin email to receive a sign-in link.
          </p>
        </div>

        {sent ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
            ✅ Magic link sent to <strong>{email}</strong>.
            Check your inbox and click the link to sign in.
          </div>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full rounded-lg border-stone-300 text-sm
                           focus:ring-magar-600 focus:border-magar-600"
              />
            </div>
            {error && (
              <p className="text-sm text-rose-600">{error}</p>
            )}
            <button
              type="submit"
              className="btn-primary w-full justify-center"
              disabled={loading}
            >
              {loading ? 'Sending…' : 'Send magic link'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// ── Suggestion queue ──────────────────────────────────────────────────────────

type FilterStatus = ReviewStatus | 'all'

function SuggestionQueue({ session }: { session: Session }) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [filter, setFilter]           = useState<FilterStatus>('pending')
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState('')

  const fetchSuggestions = useCallback(async () => {
    setLoading(true)
    setError('')
    let q = supabase
      .from('suggestions')
      .select('*')
      .order('submitted_at', { ascending: false })

    if (filter !== 'all') {
      q = q.eq('review_status', filter)
    }

    const { data, error: err } = await q
    setLoading(false)
    if (err) {
      setError(err.message)
    } else {
      setSuggestions(data ?? [])
    }
  }, [filter])

  useEffect(() => { fetchSuggestions() }, [fetchSuggestions])

  const updateStatus = async (id: string, status: ReviewStatus, adminNotes?: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase
      .from('suggestions')
      .update({
        review_status: status,
        reviewed_by:   session.user.email ?? 'admin',
        reviewed_at:   new Date().toISOString(),
        admin_notes:   adminNotes ?? null,
      } as any)
      .eq('id', id)

    if (!error) {
      setSuggestions(s => s.filter(r => r.id !== id))
    }
  }

  const signOut = () => supabase.auth.signOut()

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Admin header */}
      <header className="bg-magar-700 text-white px-4 py-4 shadow-md">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Admin — Suggestion Queue</h1>
            <p className="text-sm text-white/70">{session.user.email}</p>
          </div>
          <button onClick={signOut} className="btn-ghost text-white/80 hover:text-white">
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">

        {/* Filter tabs */}
        <div className="flex gap-2">
          {(['all', 'pending', 'accepted', 'rejected', 'merged'] as FilterStatus[]).map(f => (
            <button
              key={f}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-magar-700 text-white'
                  : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
              }`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Loading / error */}
        {loading && <p className="text-stone-400 animate-pulse">Loading suggestions…</p>}
        {error   && <p className="text-rose-600">Error: {error}</p>}

        {/* Empty state */}
        {!loading && suggestions.length === 0 && (
          <div className="text-center py-16 text-stone-400">
            <p className="text-3xl mb-3">✅</p>
            <p>No {filter === 'all' ? '' : filter} suggestions.</p>
          </div>
        )}

        {/* Suggestion cards */}
        <div className="space-y-4">
          {suggestions.map(s => (
            <SuggestionCard key={s.id} suggestion={s} onAction={updateStatus} />
          ))}
        </div>
      </main>
    </div>
  )
}

// ── Individual suggestion card ─────────────────────────────────────────────────

function SuggestionCard({
  suggestion: s,
  onAction,
}: {
  suggestion: Suggestion
  onAction: (id: string, status: ReviewStatus, notes?: string) => void
}) {
  const [notes, setNotes] = useState('')
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div
        className="flex items-start justify-between gap-4 p-4 cursor-pointer hover:bg-stone-50"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-stone-800 font-mono text-sm">{s.entry_id}</span>
            <span className="badge-candidate">{SUGGESTION_TYPE_LABELS[s.suggestion_type]}</span>
            <StatusPill status={s.review_status} />
          </div>
          {s.corrected_magar && (
            <p className="deva text-magar-700 font-medium">{s.corrected_magar}</p>
          )}
          {s.submitter_name && (
            <p className="text-sm text-stone-500">From: {s.submitter_name}</p>
          )}
        </div>
        <div className="text-xs text-stone-400 shrink-0 text-right">
          <p>{new Date(s.submitted_at).toLocaleDateString()}</p>
          <p>{expanded ? '▲' : '▼'}</p>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-stone-100 p-4 space-y-4">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Field label="Corrected Magar"  value={s.corrected_magar}  deva />
            <Field label="Corrected Nepali"  value={s.corrected_nepali}  deva />
            <Field label="Corrected English" value={s.corrected_english} />
            <Field label="Dialect note"      value={s.dialect_note}      deva />
            <Field label="Speaker notes"     value={s.speaker_notes} />
            <Field label="Submitter email"   value={s.submitter_email} />
          </dl>

          {/* Admin notes input */}
          {s.review_status === 'pending' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-stone-700">
                Admin notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                placeholder="Internal notes, reasoning…"
                className="w-full rounded-lg border-stone-300 text-sm"
              />
              <div className="flex gap-2">
                <button
                  className="btn-primary bg-emerald-600 hover:bg-emerald-500 focus:ring-emerald-600"
                  onClick={() => onAction(s.id, 'accepted', notes)}
                >
                  ✓ Accept
                </button>
                <button
                  className="btn-secondary text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => onAction(s.id, 'rejected', notes)}
                >
                  ✗ Reject
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => onAction(s.id, 'merged', notes)}
                >
                  ↩ Mark as Merged
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Field({ label, value, deva }: { label: string; value: string | null; deva?: boolean }) {
  if (!value) return null
  return (
    <div>
      <dt className="text-xs text-stone-400 uppercase tracking-wide">{label}</dt>
      <dd className={`mt-0.5 text-stone-700 ${deva ? 'font-devanagari' : ''}`}>{value}</dd>
    </div>
  )
}

function StatusPill({ status }: { status: ReviewStatus }) {
  const cls =
    status === 'accepted' ? 'badge-high-priority' :
    status === 'rejected' ? 'badge-needs-review'  :
    status === 'merged'   ? 'badge-unverified'     :
    'bg-stone-100 text-stone-600 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold'
  return <span className={cls}>{status}</span>
}
